import { formatDate } from '@angular/common';
import { SolText } from '../models/core/sol-text';
import { ObjectTools } from './object-tools';

/** One relative-date bucket. */
export interface SolDateGroup<T> {
  readonly id: string;
  readonly label: SolText;
  readonly items: readonly T[];
}

interface DateGroupDef {
  readonly id: string;
  readonly labelKey: string;
  readonly maxDaysAgo: number;
}

export class DateTools {
  /** Default locale for date formatting. Used as initial value before LocaleService provides the real locale. */
  public static readonly DEFAULT_LOCALE = 'en-GB';

  /** Relative-date buckets, youngest first. `maxDaysAgo` is inclusive, so a date on a boundary lands in the younger bucket. */
  private static readonly DATE_GROUPS: readonly DateGroupDef[] = [
    { id: 'today', labelKey: 'sol-date.today', maxDaysAgo: 0 },
    { id: 'yesterday', labelKey: 'sol-date.yesterday', maxDaysAgo: 1 },
    { id: 'last-week', labelKey: 'sol-date.last-week', maxDaysAgo: 7 },
    { id: 'last-month', labelKey: 'sol-date.last-month', maxDaysAgo: 30 },
    { id: 'older', labelKey: 'sol-date.older', maxDaysAgo: Number.MAX_VALUE },
  ];

  /**
   * Returns the current date
   */
  public static now(): Date {
    return new Date();
  }

  /**
   * Converts the given date to UTC iso string.
   */
  public static toUtcString(date: Date | undefined): string | undefined {
    return ObjectTools.isNull(date) ? undefined : date!.toISOString();
  }

  /**
   * Convert a given ISO string to a Date object, reading it as UTC when it carries no zone.
   * An empty value gives the fallback.
   */
  public static fromUtcString(value: string | null | undefined, fallback: Date): Date;
  public static fromUtcString(value: string | null | undefined): Date | undefined;
  public static fromUtcString(value: string | null | undefined, fallback?: Date): Date | undefined {
    if (!value) {
      return fallback;
    }
    const iso = value.replace(' ', 'T');
    return new Date(/([Zz]|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : iso + 'Z');
  }

  public static format(
    value: Date | undefined,
    format: string,
    locale: string = DateTools.DEFAULT_LOCALE,
  ): string | undefined {
    return value === undefined ? undefined : formatDate(value, format, locale);
  }

  public static parse(value: string | undefined, format: 'yyyy-MM-dd' | 'dd-MM-yyyy'): Date | undefined {
    if (!value) {
      return undefined;
    }

    const match =
      format === 'dd-MM-yyyy' ? /^(\d{2})-(\d{2})-(\d{4})$/.exec(value) : /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) {
      return undefined;
    }

    const [year, month, day] =
      format === 'dd-MM-yyyy' ? [match[3], match[2], match[1]] : [match[1], match[2], match[3]];
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  /**
   * Return the start of day of the given date.
   */
  public static startOfDay(candidate?: Date): Date {
    const date = candidate !== undefined ? candidate : new Date();
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  /**
   * Returns the difference between two dates in the given unit.
   * Default is milliseconds. Rounds by default.
   */
  public static diff(
    a: Date,
    b: Date,
    unit: 'ms' | 'seconds' | 'minutes' | 'hours' | 'days' = 'ms',
    round: boolean = true,
  ): number {
    const deltaMs = a.getTime() - b.getTime();
    let value: number;

    switch (unit) {
      case 'seconds':
        value = deltaMs / 1000;
        break;
      case 'minutes':
        value = deltaMs / (1000 * 60);
        break;
      case 'hours':
        value = deltaMs / (1000 * 60 * 60);
        break;
      case 'days':
        value = deltaMs / (1000 * 60 * 60 * 24);
        break;
      default:
        value = deltaMs;
    }

    return round ? Math.round(value) : value;
  }

  public static toRelativeText(date: Date, now: Date = new Date(), locale: string): SolText {
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffSeconds < 60) {
      return SolText.of('sol-date.just-now');
    } else if (diffMinutes < 60) {
      return SolText.of('sol-date.minutes-ago', { count: diffMinutes });
    } else if (diffHours < 24) {
      return SolText.of('sol-date.hours-ago', { count: diffHours });
    } else if (diffDays < 7) {
      return SolText.of('sol-date.days-ago', { count: diffDays });
    } else {
      return DateTools.toAbsoluteText(date, locale);
    }
  }

  /** Locale-aware absolute date (e.g. "26 Jul 2026" in en-GB, "Jul 26, 2026" in en-US). */
  public static toAbsoluteText(date: Date, locale: string): SolText {
    return SolText.ofRaw(
      new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(date),
    );
  }

  /** Locale-aware short month name (e.g., "Oct" in en-US, "oct." in fr). */
  public static formatMonthShort(month: number, locale: string): string {
    return new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' }).format(
      new Date(Date.UTC(2000, month - 1, 1)),
    );
  }

  /** Locale-aware month + day (e.g., "Oct 7" in en-US, "7 oct." in fr). */
  public static formatMonthDay(month: number, day: number, locale: string): string {
    return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(
      new Date(Date.UTC(2000, month - 1, day)),
    );
  }

  /** Compute the Monday of an ISO week. Returns {year, month (1-12), day}. */
  public static mondayOfIsoWeek(isoYear: number, isoWeek: number): { year: number; month: number; day: number } {
    // Jan 4 is always in ISO week 1.
    const jan4 = new Date(Date.UTC(isoYear, 0, 4));
    const dayOfWeek = jan4.getUTCDay() || 7; // Mon=1 .. Sun=7
    const monday1 = new Date(Date.UTC(isoYear, 0, 4 - dayOfWeek + 1));
    monday1.setUTCDate(monday1.getUTCDate() + (isoWeek - 1) * 7);
    return { year: monday1.getUTCFullYear(), month: monday1.getUTCMonth() + 1, day: monday1.getUTCDate() };
  }

  /** Buckets items into relative-date groups, newest first. Empty groups are dropped. */
  public static toDateGroups<T>(
    items: readonly T[],
    dateOf: (item: T) => Date,
    now: Date = new Date(),
  ): readonly SolDateGroup<T>[] {
    if (items.length === 0) {
      return [];
    }
    const startOfToday = DateTools.startOfDay(now);
    const buckets = DateTools.DATE_GROUPS.map(group => ({ def: group, items: [] as T[] }));
    const sorted = [...items].sort((a, b) => dateOf(b).getTime() - dateOf(a).getTime());
    for (const item of sorted) {
      // Rounded, so a bucket boundary crossing a DST change still lands on a whole number of days.
      const daysAgo = DateTools.diff(startOfToday, DateTools.startOfDay(dateOf(item)), 'days');
      const bucket = buckets.find(candidate => daysAgo <= candidate.def.maxDaysAgo) ?? buckets[buckets.length - 1];
      bucket.items.push(item);
    }
    return buckets
      .filter(bucket => bucket.items.length > 0)
      .map(bucket => ({ id: bucket.def.id, label: SolText.of(bucket.def.labelKey), items: bucket.items }));
  }

  public static compare(a: Date | undefined, b: Date | undefined): number {
    if (ObjectTools.isNull(a)) {
      return ObjectTools.isNull(b) ? 0 : -1;
    } else {
      return ObjectTools.isNull(b) ? 1 : a!.getTime() - b!.getTime();
    }
  }
}
